import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { SITE, IMAGES } from '../mock';

const LINKS = [
  { to: '/', label: 'Home' },
  { to: '/ueber-uns', label: 'Über uns' },
  {
    label: 'Angebot',
    children: [
      { to: '/angebot', label: 'Übersicht' },
      { to: '/immobilien', label: 'Immobilien' },
      { to: '/alphalane', label: 'Alphalane AI' },
      { to: '/vorsorge', label: 'Vorsorgegelder finden' },
      { to: '/edelmetalle', label: 'Edelmetalle' },
    ],
  },
  { to: '/kontakt', label: 'Kontakt' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [submenu, setSubmenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSubmenu(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#0a0a0c]/85 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 md:h-28 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3.5 group">
          <img
            src={IMAGES.logoMark}
            alt="Moonshine Solutions"
            className="h-12 md:h-14 w-auto object-contain drop-shadow-[0_0_20px_rgba(212,181,116,0.35)] transition-transform duration-500 group-hover:scale-[1.05]"
          />
          <div className="hidden sm:flex flex-col leading-[1.05]">
            <span className="font-cinzel font-medium text-[18px] md:text-[20px] tracking-[0.22em] text-[#e8d5a8]">MOONSHINE</span>
            <span className="font-cinzel font-normal text-[10px] md:text-[11px] tracking-[0.42em] text-[#b8a576] mt-1 pl-[2px]">SOLUTIONS</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) =>
            l.children ? (
              <div
                key={l.label}
                className="relative"
                onMouseEnter={() => setSubmenu(true)}
                onMouseLeave={() => setSubmenu(false)}
              >
                <button className="flex items-center gap-1.5 text-sm tracking-wide text-[#cfc6b5] hover:text-[#e8d5a8] transition-colors">
                  {l.label}
                  <ChevronDown size={14} className={`transition-transform ${submenu ? 'rotate-180' : ''}`} />
                </button>
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${
                    submenu ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                  }`}
                >
                  <div className="min-w-[240px] rounded-xl bg-[#13130f]/95 backdrop-blur-xl border border-[#d4b574]/20 shadow-2xl shadow-black/60 p-2">
                    {l.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        className={({ isActive }) =>
                          `block px-4 py-2.5 text-sm rounded-lg transition-all ${
                            isActive
                              ? 'bg-[#d4b574]/10 text-[#e8d5a8]'
                              : 'text-[#cfc6b5] hover:bg-white/5 hover:text-[#e8d5a8]'
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive ? 'text-[#e8d5a8]' : 'text-[#cfc6b5] hover:text-[#e8d5a8]'
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={SITE.vorsorgecheckUrl}
            target="_blank"
            rel="noreferrer"
            className="px-5 py-2.5 text-sm rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors whitespace-nowrap"
          >
            Finanzcheck starten
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#e8d5a8]"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? 'max-h-[600px] border-t border-white/5' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-4 bg-[#0a0a0c]/95 backdrop-blur-xl flex flex-col gap-1">
          {LINKS.map((l) =>
            l.children ? (
              <div key={l.label} className="py-1">
                <div className="text-xs uppercase tracking-[0.2em] text-[#7a7060] py-2 px-3">{l.label}</div>
                {l.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-sm rounded-lg ${
                        isActive ? 'bg-[#d4b574]/10 text-[#e8d5a8]' : 'text-[#cfc6b5]'
                      }`
                    }
                  >
                    {c.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm rounded-lg ${
                    isActive ? 'text-[#e8d5a8] bg-white/5' : 'text-[#cfc6b5]'
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <a
            href={SITE.vorsorgecheckUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 text-center px-5 py-3 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium"
          >
            Finanzcheck starten
          </a>
        </div>
      </div>
    </header>
  );
}
